import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { Form, Button, Row, Col, Image, Card } from 'react-bootstrap';
import { Upload, X, Trash2, CheckCircle2, Plus, ArrowRight } from 'lucide-react';
import { formatPhoneBrazilian, unformatPhone } from '../../utils/formatters';
import { useAlert } from '../../context/AlertContext';

interface Professional {
    id: number;
    name: string;
    phone: string;
    bio: string;
    photo: string | null;
}

interface Props {
    onNext: () => void;
}

export const ProfessionalsStep: React.FC<Props> = ({ onNext }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [professionals, setProfessionals] = useState<Professional[]>([]);
    const [formData, setFormData] = useState({ name: '', phone: '', bio: '' });
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const { showAlert } = useAlert();

    useEffect(() => {
        fetchProfessionals();
    }, []);

    const fetchProfessionals = async () => {
        try {
            const response = await api.get('/professionals/');
            setProfessionals(response.data);
        } catch (error) {
            console.error('Error fetching professionals:', error);
        } finally {
            setFetching(false);
        }
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const submitData = new FormData();
            submitData.append('name', formData.name.toUpperCase());
            submitData.append('phone', formData.phone);
            submitData.append('bio', formData.bio ? formData.bio.toUpperCase() : '');

            const response = await api.post('/professionals/', submitData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const newId = response.data.id;

            if (photoFile) {
                const photoData = new FormData();
                photoData.append('photo', photoFile);
                await api.patch(`/professionals/${newId}/`, photoData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }

            setFormData({ name: '', phone: '', bio: '' });
            removePhoto();
            await fetchProfessionals();
            setShowForm(false);
        } catch (error) {
            console.error('Error adding professional:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja remover este profissional?', {
            type: 'warning',
            title: 'Remover Profissional',
            confirmable: true,
            confirmText: 'Remover',
            cancelText: 'Cancelar',
            confirmVariant: 'danger',
            onConfirm: async () => {
                try {
                    setLoading(true);
                    await api.delete(`/professionals/${id}/`);
                    await fetchProfessionals();
                } catch (error) {
                    console.error('Error deleting professional:', error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    if (fetching) {
        return <div className="text-center p-5">Carregando...</div>;
    }

    const showAddForm = showForm || professionals.length === 0;

    return (
        <div>
            <div className="step-header">
                <h3>Cadastro de Profissionais</h3>
                <p className="text-muted">Adicione os profissionais que atendem no seu negócio. Você precisa de pelo menos 1 para continuar.</p>
            </div>

            {showAddForm ? (
                <Card className="mb-4 bg-light border-0">
                    <Card.Body>
                        <h5 className="mb-3">Adicionar Novo</h5>
                        <Form onSubmit={handleAdd}>
                            <Row>
                                <Col xs={12} md={3} className="text-center mb-3 mb-md-0">
                                    <div className="mb-2">
                                        {photoPreview ? (
                                            <div className="position-relative d-inline-block" onClick={() => fileInputRef.current?.click()} style={{ cursor: 'pointer' }}>
                                                <Image src={photoPreview} alt="Preview" roundedCircle style={{ width: '100px', height: '100px', objectFit: 'cover', border: '3px solid white' }} />
                                                <Button variant="danger" size="sm" className="position-absolute top-0 end-0 rounded-circle p-1" onClick={(e: any) => { e.stopPropagation(); removePhoto(); }} style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <X size={14} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center bg-white rounded-circle mx-auto" onClick={() => fileInputRef.current?.click()} style={{ width: '100px', height: '100px', border: '2px dashed #dee2e6', cursor: 'pointer' }}>
                                                <Upload size={30} className="text-muted" />
                                            </div>
                                        )}
                                        <Form.Control ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                                    </div>
                                    <small className="text-muted">Foto (Opcional)</small>
                                </Col>
                                <Col xs={12} md={9}>
                                    <Row>
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Nome Completo *</Form.Label>
                                                <Form.Control value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required style={{ textTransform: 'uppercase' }} />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Telefone *</Form.Label>
                                                <Form.Control value={formatPhoneBrazilian(formData.phone)} onChange={(e) => setFormData({ ...formData, phone: unformatPhone(e.target.value) })} required />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Mini-bio (Opcional)</Form.Label>
                                        <Form.Control as="textarea" rows={2} value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} />
                                    </Form.Group>
                                    <div className="d-grid d-md-block">
                                        <Button variant="primary" type="submit" disabled={loading || !formData.name || !formData.phone}>
                                            {loading ? 'Adicionando...' : 'Adicionar Profissional'}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
            ) : (
                <Button 
                    variant="outline-primary" 
                    onClick={() => setShowForm(true)} 
                    className="mb-4 d-flex align-items-center justify-content-center gap-2 w-100 w-md-auto"
                >
                    <Plus size={20} />
                    Adicionar Profissional
                </Button>
            )}

            {professionals.length > 0 && (
                <div className="professionals-list mb-4">
                    <h5 className="mb-3">Profissionais Adicionados ({professionals.length})</h5>
                    {professionals.map(prof => (
                        <div key={prof.id} className="item-card">
                            <div className="d-flex align-items-center flex-grow-1 min-width-0">
                                {prof.photo ? (
                                    <Image src={prof.photo} roundedCircle style={{ width: '40px', height: '40px', objectFit: 'cover', border: '1px solid #ccc' }} className="me-3 flex-shrink-0" />
                                ) : (
                                    <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '40px', height: '40px', fontWeight: 'bold', color: '#666' }}>
                                        {prof.name.charAt(0)}
                                    </div>
                                )}
                                <div className="min-width-0">
                                    <h6 className="mb-0 text-truncate">{prof.name}</h6>
                                    <small className="text-muted">{formatPhoneBrazilian(prof.phone)}</small>
                                </div>
                            </div>
                            <Button variant="link" onClick={() => handleDelete(prof.id)} disabled={loading} className="text-secondary p-0 ms-2 flex-shrink-0">
                                <Trash2 size={18} strokeWidth={1.5} />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {professionals.length > 0 && (
                <div className="step-navigation">
                    <div className="step-navigation-status text-success">
                        <CheckCircle2 size={20} /> Pronto para prosseguir
                    </div>
                    <div className="step-navigation-buttons">
                        <Button className="btn-primary-premium btn-step-next" onClick={onNext}>
                            Próximo Passo <ArrowRight size={18} />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
